/**
 * finance-adapter.ts — finance 分维度 / 产品销量「真 API DTO（多币种） → 原型富集行」塑形
 *
 * 「接真」核心：FinancePage（design/Novel-admin/src/pages/finance.jsx 的 DimPanel / ProductSales）
 * 仍消费原型 camelCase 富集行 FinanceRow / ProductSales（~/data/mock/types.ts），而真实后端返回
 * snake_case **多币种** DTO（FinanceDimensionStatsResponse / FinanceProductSalesResponse，`by_currency` 分组，
 * 见 packages/shared/src/schemas/finance.ts + dev-docs `02-API契约/A1-运营财务.md`，张培 #119）。
 * 原币不归一（MVP），故 adapter 以**单个币种组**为单位塑形，页面用币种选择器切换（useFinance 暴露 currencies）。
 *
 * 单位 / 口径换算（真 DTO → 原型行）：
 * - 金额：真 DTO 用 *_minor_units（bigint 原币最小单位 / 分，string）；原型行用主单位 number → 按该币种 ISO exponent 换算。
 * - 占比：真 DTO 用 *_ratio（0~1 小数 string）；原型行用百分数 number（如 62.4）→ × 100。
 * - 被动退款率：真 DTO `passive_refund_rate`（0~1，维度内 REFUND笔数/PURCHASE笔数）→ 原型 refundRate 百分数 → × 100。
 *
 * 缺字段处理（真 DTO 没有、原型有）：见 adaptFinanceProductSales opts（name/period/price）。
 */

import type {
  FinanceCurrencyDimensionGroup,
  FinanceCurrencyProductGroup,
} from '@novel-admin/shared';
import type { FinanceRow, ProductSales } from '~/data/mock/types';
import { currencyMinorToMajorNumber } from '~/lib/format';

/** 0~1 比率（string）→ 百分数（number，如 0.624 → 62.4）。非法落 0。 */
function ratioToPercent(ratio: string): number {
  const n = Number(ratio);
  return Number.isFinite(n) ? n * 100 : 0;
}

/**
 * 单币种分维度组 → FinanceRow[]（DimPanel 表格行）。
 * by-platform / by-channel 同形状，dimension_key 即原型行 key（'iOS'/'Android'/'OTHER' 或 'IAP'/'STRIPE'）。
 */
export function adaptFinanceDimensionRows(group: FinanceCurrencyDimensionGroup): FinanceRow[] {
  return group.rows.map((r) => ({
    key: r.dimension_key,
    orders: r.charge_orders,
    users: r.charge_users,
    revenue: currencyMinorToMajorNumber(r.total_revenue_minor_units, group.currency),
    // 退款率（passive_refund_rate）= 维度内 REFUND笔数/PURCHASE笔数 → 百分数
    refundRate: ratioToPercent(r.passive_refund_rate),
    share: ratioToPercent(r.revenue_share_ratio),
  }));
}

/** 产品销量 source(kind) → 原型中文类型。 */
const PRODUCT_KIND_ZH: Record<string, string> = {
  CHARGE: '充值',
  SUBSCRIPTION: '订阅',
};

/** adapter 可选注入项：真 DTO 缺、原型展示需要的产品元信息（按 product_id 映射）。 */
export type FinanceProductAdapterOptions = {
  /** product_id → 产品展示名；缺省用 product_id 本身。 */
  nameMap?: Record<string, string>;
  /** product_id → 订阅周期（如 'P1M'）；缺省 null。 */
  periodMap?: Record<string, string | null>;
  /** product_id → 产品单价（主单位）；缺省 null（前端渲染 '—'，不用 aov 冒充单价）。 */
  priceMap?: Record<string, number>;
};

/**
 * 单币种产品组 → ProductSales[]（ProductSales 页表格行）。
 * 收入倒序已由后端保证，这里仅做字段对照 + 单位换算；price/name/period 缺字段经 opts 注入或合理代理。
 */
export function adaptFinanceProductSales(
  group: FinanceCurrencyProductGroup,
  opts: FinanceProductAdapterOptions = {},
): ProductSales[] {
  return group.rows.map((r) => {
    const aov = currencyMinorToMajorNumber(r.avg_order_value_minor_units, group.currency);
    return {
      name: opts.nameMap?.[r.product_id] ?? r.product_id,
      kind: PRODUCT_KIND_ZH[r.kind] ?? r.kind,
      period: opts.periodMap?.[r.product_id] ?? null,
      // 真 DTO 无 list price（S006 不返单价，product_metadata 不收 price）→ 无真值时 null（前端渲染 '—'）。
      // 不用 aov（平均客单价 = 收入/订单数）冒充单价：两者口径不同，aov 已单列展示，price 列再放 aov = 重复+误导。
      price: opts.priceMap?.[r.product_id] ?? null,
      orders: r.orders,
      users: r.users,
      revenue: currencyMinorToMajorNumber(r.total_revenue_minor_units, group.currency),
      aov,
      sharePct: ratioToPercent(r.revenue_share_ratio),
    };
  });
}
